// @ts-strict-ignore
import React, { useCallback, useMemo } from 'react';
import {
  Box,
  Icon,
  IconName,
  Text,
  Tooltip,
  useSnackBar,
} from '@usealloy/component-library';

import CopyToClipboard from '~/components/CopyToClipboard';
import { RebrandColors, GRAY } from '~/constants';
import { API_ORDERED_FIELD_NAMES } from '~/evaluations/helpers/constants';
import { buildUrlParamsString } from '~/helpers/api';
import { formatPhoneNumber, snakeToCaps } from '~/helpers/formatting';
import { handleClipboardCopyText } from '~/helpers/messages';
import { formatterMixin } from '~/helpers/mixins';
import { routes } from '~/helpers/routes';
import type { Evaluation } from '~/entities/types';

import {
  YourCustomerItemContainer,
  ServiceMatched,
  ServiceUnmatched,
  VelocityWarning,
  NoChangeChip,
  UpdatedValue,
  FormattedRecord,
} from './styles';
import { VelocityWarningText } from './VelocityWarningText';
import { notVerifiedText, vendorVerifiedText } from './helpers';

const getOrderedFormattedFields = formatted => {
  const orderedFieldNames = Object.values(API_ORDERED_FIELD_NAMES).flat();
  const extraFields = Object.keys(formatted)
    .filter(fieldName => !orderedFieldNames.includes(fieldName));
  return orderedFieldNames.concat(extraFields);
};

const formatName = name => {
  if (name === 'dob') {
    return 'Birth Date';
  }

  if (name === 'ssn') {
    return 'SSN';
  }

  if (name === 'document_national_id') {
    return 'National ID';
  }

  if (name === 'ip_address') {
    return 'IP Address';
  }

  return snakeToCaps(name);
};

const formatData = ({
  data,
  name,
  categoryFields,
  formatter,
}) => {
  switch (name) {
    case 'phone':
      return formatPhoneNumber(formatter.formatPhone(data[categoryFields[0]]));
    case 'address':
      return formatter.formatAddress('person', data);
    case 'business_address':
      return formatter.formatAddress('business', data);
    case 'business_owner':
      return formatter.formatBusinessOwner(data);
    case 'dob':
      return formatter.formatBirthDate(data.birth_date);
    case 'name':
      return formatter.formatName(data);
    case 'email':
      return (data.email_address ?? '').toUpperCase();
    case 'ssn':
      return data.document_ssn;
    case 'ip_address':
      return data.ip_address_v4 || data.ip_address_v6;
    default:
      return categoryFields.map(field => (
        <span key={field}>
          {data[field]}
        </span>
      ));
  }
};

interface YourCustomerServicesProps {
  name: string;
  matched: Array<string>;
  unmatched: Array<string>;
  hasChanged: boolean;
  hasVelocityWarning: boolean;
  duplicateEntitiesCount: number;
  duplicateEvaluationsCount: number;
  onDuplicatePiiClick: () => void;
  useAbbreviatedTooltips?: boolean;
}

const YourCustomerServices: React.FC<YourCustomerServicesProps> = ({
  name,
  matched,
  unmatched,
  hasChanged,
  hasVelocityWarning,
  duplicateEntitiesCount,
  duplicateEvaluationsCount,
  onDuplicatePiiClick,
  useAbbreviatedTooltips = false,
}) => (
  <>
    {matched.map(service => (
      <Tooltip
        key={service}
        side="top"
        delayDuration={0}
        content={(
          <Text
            size={1}
            lineHeight={2}
            style={{
              textAlign: 'center',
              color: 'var(--cl-text-accent)',
            }}
          >
            {useAbbreviatedTooltips ? (
              'Verified'
            ) : (
              <>
                {`${hasChanged ? 'Updated' : 'On Record'} ${name}`}
                <br />
                was verified
              </>
            )}
          </Text>
        )}
      >
        <ServiceMatched
          justifyContent="flex-start"
          direction="row"
          gap={1}
        >
          <Icon
            icon={IconName.CircleCheckFilled}
            size="small"
            color={RebrandColors.GREEN_300}
          />
          <Text size={1}>{service}</Text>
        </ServiceMatched>
      </Tooltip>
    ))}
    {unmatched.map(service => (
      <Tooltip
        key={service}
        side="top"
        delayDuration={0}
        content={(
          <Text
            size={1}
            lineHeight={2}
            style={{
              textAlign: 'center',
              color: 'var(--cl-text-accent)',
            }}
          >
            {useAbbreviatedTooltips ? (
              'Not verified'
            ) : (
              <>
                {`Updated ${name}`}
                <br />
                was not verified
              </>
            )}
          </Text>
        )}
      >
        <ServiceUnmatched
          key={service}
          justifyContent="flex-start"
          direction="row"
          gap={1}
        >
          <Icon
            icon={IconName.CircleMinusFilled}
            size="small"
            color={GRAY.gray700}
          />
          <Text
            size={1}
            style={{
              color: 'var(--cl-gray-700)',
            }}
          >
            {service}
          </Text>
        </ServiceUnmatched>
      </Tooltip>
    ))}
    {hasVelocityWarning && (
      <Tooltip
        side="top"
        delayDuration={0}
        content={(
          <VelocityWarningText
            duplicateEntitiesCount={duplicateEntitiesCount}
            duplicateEvaluationsCount={duplicateEvaluationsCount}
            name={name}
            onDuplicatePiiClick={onDuplicatePiiClick}
          />
        )}
      >
        <VelocityWarning
          justifyContent="flex-start"
          direction="row"
          gap={1}
        >
          <Icon
            icon={IconName.TriangleWarning}
            size="small"
            color={RebrandColors.ORANGE_300}
          />
          <Text size={1}>
            Velocity Warning
          </Text>
        </VelocityWarning>
      </Tooltip>
    )}
  </>
);

interface YourCustomerItemProps {
  name: string;
  displayName?: string;
  formatted: Evaluation['final_response']['formatted'];
  matching: Evaluation['final_response']['matching'];
  entityPiiPrevious: Evaluation['final_response']['formatted'];
  duplicateEvaluationsCount: number;
  duplicateEntitiesCount: number;
  isSandbox: boolean;
  useExistingPiiOnly?: boolean;
}

export const YourCustomerItem: React.FC<YourCustomerItemProps> = ({
  name,
  displayName = '',
  formatted,
  matching,
  entityPiiPrevious,
  duplicateEvaluationsCount,
  duplicateEntitiesCount,
  isSandbox,
  useExistingPiiOnly = false,
}) => {
  const addSnackBar = useSnackBar();
  const formattedName = formatName(displayName || name);
  const { matched = [], unmatched = [] } = matching[name] ?? {};
  const hasVerifiedBy = matched.length > 0 || unmatched.length > 0;

  const categoryFields = useMemo(() => {
    // Get all relevant fields for this category
    // those prepended with the appropriate category and not including country codes
    const orderedFormattedFields = getOrderedFormattedFields(formatted);

    let fields = orderedFormattedFields.filter(field => (
      (field.indexOf(name) === 0 && !field.includes('country_code'))
        || (name === 'dob' && field.indexOf('birth_date') > -1)
    ));

    // remove email_address from address category
    // make sure to create email as its own category
    if (name === 'address') {
      fields = fields.filter(field => field !== 'email_address');
    }

    if (name === 'business_id') {
      fields = ['business_federal_ein'];
    }

    if (name === 'ssn') {
      fields = ['document_ssn'];
    }

    return fields;
  }, [formatted]);

  const formattedRecord = useMemo(() => {
    const formatter = formatterMixin();
    const formattedData = formatData({
      data: entityPiiPrevious,
      name,
      categoryFields,
      formatter,
    });
    return formattedData;
  }, [name, entityPiiPrevious, categoryFields]);

  const formattedUpdated = useMemo(() => {
    const formatter = formatterMixin();

    return formatData({
      data: formatted,
      name,
      categoryFields,
      formatter,
    });
  }, [name, formatted, categoryFields]);

  const hasChanged = useMemo(() => {
    let formattedRecordVal = formattedRecord;
    let formattedUpdatedVal = formattedUpdated;

    if (Array.isArray(formattedRecord)) {
      formattedRecordVal = formattedRecord.map(({ props: { children } }) => children).join('\n');
    }

    if (Array.isArray(formattedUpdated)) {
      formattedUpdatedVal = formattedUpdated.map(({ props: { children } }) => children).join('\n');
    }

    // address-specific validation
    if (name === 'address') {
      // remove trailing 4 digits in zip code (i.e 62704-1234 -> 62704)
      const normalizeZip = text => text.replace(/\b(\d{5})-\d{4}\b/g, '$1');
      formattedRecordVal = normalizeZip(formattedRecordVal);
      formattedUpdatedVal = normalizeZip(formattedUpdatedVal);
    }

    return formattedRecordVal !== formattedUpdatedVal;
  }, [formattedRecord, formattedUpdated]);
  const wasVerified = matched.length > 0;

  const onDuplicatePiiClick = useCallback(() => {
    const params = { filterBy: {}, searchBy: {} };
    // Only link to entities page if there are no duplicate evaluations,
    // otherwise - link to evaluations page
    if (duplicateEvaluationsCount === null) {
      if (isSandbox) {
        params.filterBy = { sandbox: 'true' };
      }
      params.searchBy = formatted[categoryFields[0]];
      window.location.href = `${routes.entities.list}${buildUrlParamsString(params, true)}`;
    } else {
      params.searchBy = { [name]: formatted[categoryFields[0]] };
      params.filterBy = { sandbox: 'all' };
      window.location.href = `${routes.evaluations.list}${buildUrlParamsString(params, true)}`;
    }
  }, [duplicateEvaluationsCount, formattedUpdated, isSandbox]);

  const hasVelocityWarning = duplicateEntitiesCount > 0;

  if (useExistingPiiOnly) {
    return (
      <YourCustomerItemContainer
        direction="row"
        justifyContent="flex-start"
        alignItems="flex-start"
        gap={5}
      >
        <Box
          direction="column"
          justifyContent="flex-start"
          alignItems="flex-start"
          gap={4}
        >
          <Text
            size={2}
            style={{
              color: 'var(--cl-gray-700)',
            }}
          >
            {formattedName}
          </Text>
          <FormattedRecord
            size={2}
            lineHeight={4}
          >
            {formattedRecord}
          </FormattedRecord>
        </Box>
        <Box
          direction="column"
          justifyContent="flex-start"
          alignItems="flex-start"
          gap={1}
        >
          <YourCustomerServices
            name={formattedName}
            matched={matched}
            unmatched={unmatched}
            hasChanged={hasChanged}
            hasVelocityWarning={hasVelocityWarning}
            duplicateEntitiesCount={duplicateEntitiesCount}
            duplicateEvaluationsCount={duplicateEvaluationsCount}
            onDuplicatePiiClick={onDuplicatePiiClick}
            useAbbreviatedTooltips
          />
        </Box>
      </YourCustomerItemContainer>
    );
  }

  return (
    <YourCustomerItemContainer
      direction="column"
      justifyContent="flex-start"
      alignItems="flex-start"
      gap={4}
    >
      <Text size={2} weight={700}>
        {formattedName}
      </Text>
      <Box
        direction="column"
        justifyContent="flex-start"
        gap={3}
      >
        <Box
          direction="row"
          justifyContent="flex-start"
          alignItems="flex-start"
          gap={4}
        >
          <Box
            direction="column"
            justifyContent="flex-start"
            alignItems="flex-start"
            gap={2}
            style={{
              maxWidth: '50%',
            }}
          >
            <Box
              direction="row"
              justifyContent="flex-start"
              gap={1}
            >
              {hasChanged ? (
                <Text
                  size={2}
                  style={{
                    color: 'var(--cl-gray-700)',
                  }}
                >
                  Previous Record
                </Text>
              ) : (
                <>
                  <Text
                    size={2}
                    style={{
                      color: 'var(--cl-gray-700)',
                    }}
                  >
                    On Record
                  </Text>
                  <Tooltip content={wasVerified ? vendorVerifiedText(name) : notVerifiedText(name)}>
                    <Icon
                      icon={wasVerified ? IconName.CircleCheckFilled : IconName.CircleMinusFilled}
                      size="small"
                      color={wasVerified ? RebrandColors.GREEN_300 : GRAY.gray700}
                    />
                  </Tooltip>
                  {hasVelocityWarning && (
                    <Icon
                      icon={IconName.TriangleWarning}
                      size="small"
                      color={RebrandColors.ORANGE_300}
                    />
                  )}
                </>
              )}
            </Box>
            <FormattedRecord
              size={2}
              lineHeight={4}
              style={{
                minHeight: 'var(--cl-space-6)',
                alignItems: 'center',
                display: 'flex',
              }}
            >
              <CopyToClipboard
                data={formattedRecord}
                category={name}
                onCopy={(event: ClipboardJS.Event) => {
                  addSnackBar({
                    title: handleClipboardCopyText(event),
                    type: 'success',
                  });
                }}
              />
            </FormattedRecord>
          </Box>
          <Box
            direction="column"
            justifyContent="flex-start"
            alignItems="flex-start"
            gap={2}
            style={{
              maxWidth: '50%',
            }}
          >
            <Box
              direction="row"
              justifyContent="flex-start"
              gap={1}
            >
              <Text
                size={2}
                style={{
                  color: 'var(--cl-gray-700)',
                }}
              >
                Updated
              </Text>
              {hasChanged && (
                <>
                  <Tooltip content={wasVerified ? vendorVerifiedText(name) : notVerifiedText(name)}>
                    <Icon
                      icon={wasVerified ? IconName.CircleCheckFilled : IconName.CircleMinusFilled}
                      size="small"
                      color={wasVerified ? RebrandColors.GREEN_300 : GRAY.gray700}
                    />
                  </Tooltip>
                  {hasVelocityWarning && (
                    <Tooltip
                      content={(
                        <VelocityWarningText
                          duplicateEntitiesCount={duplicateEntitiesCount}
                          duplicateEvaluationsCount={duplicateEvaluationsCount}
                          name={name}
                          onDuplicatePiiClick={onDuplicatePiiClick}
                        />
                      )}
                    >
                      <Icon
                        icon={IconName.TriangleWarning}
                        size="small"
                        color={RebrandColors.ORANGE_300}
                      />
                    </Tooltip>
                  )}
                </>
              )}
            </Box>
            {hasChanged ? (
              <UpdatedValue
                size={2}
              >
                <CopyToClipboard data={formattedUpdated} category={name} />
              </UpdatedValue>
            ) : (
              <NoChangeChip
                disabled
              >
                No Change
              </NoChangeChip>
            )}
          </Box>
        </Box>
        <Box
          direction="row"
          justifyContent="flex-start"
          alignItems="flex-start"
          gap={4}
        >
          {hasChanged && <Box>{null}</Box>}
          {hasVerifiedBy && (
            <Box
              direction="column"
              justifyContent="flex-start"
              alignItems="flex-start"
              gap={2}
            >
              <Text
                size={2}
                style={{
                  color: 'var(--cl-gray-700)',
                }}
              >
                Verified By
              </Text>
              <Box
                direction="column"
                justifyContent="flex-start"
                alignItems="flex-start"
                gap={2}
              >
                <YourCustomerServices
                  name={formattedName}
                  matched={matched}
                  unmatched={unmatched}
                  hasChanged={hasChanged}
                  hasVelocityWarning={hasVelocityWarning}
                  duplicateEntitiesCount={duplicateEntitiesCount}
                  duplicateEvaluationsCount={duplicateEvaluationsCount}
                  onDuplicatePiiClick={onDuplicatePiiClick}
                />
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </YourCustomerItemContainer>
  );
};
